import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TIER_COLORS = {
  Bronze: '#cd7f32',
  Silver: '#9ca3af',
  Gold: '#f59e0b',
  Platinum: '#06b6d4',
  Diamond: '#8b5cf6',
  Elite: '#E5202A',
};

const PAGE_SIZE = 12;

const formatDiamonds = (n) => {
  if (!n && n !== 0) return '—';
  return n?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatDollars = (n) => {
  if (n == null) return '—';
  return `$${Math.round(n).toLocaleString('en-US')}`;
};

const formatLiveHours = (raw) => {
  if (raw == null) return '—';
  if (typeof raw === 'string' && raw.includes('h')) return raw;
  const totalMinutes = Math.round(Number(raw) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

const TierBadge = ({ tier }) => {
  if (!tier) return <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>;
  const label = typeof tier === 'number' ? `T${tier}` : String(tier);
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}>
      {label}
    </span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 border text-xs" style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}>
      <p className="text-white/50 mb-1">{label}</p>
      <p className="font-bold text-white">💎 {formatDiamonds(payload?.[0]?.value)}</p>
    </div>
  );
};

const ManagerMonthlyHistory = ({ monthlyData, loading, hideNetAgency = false }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('period');
  const [sortDir, setSortDir] = useState('desc');

  const chartData = useMemo(() => {
    return [...(monthlyData || [])]?.sort((a, b) => a?.period?.localeCompare(b?.period))?.slice(-12)?.map((r) => ({
      period: r?.period?.slice(0, 7) || '',
      diamonds: r?.diamonds || 0,
      tier: r?.tier_p || '',
    }));
  }, [monthlyData]);

  const sortedData = useMemo(() => {
    return [...(monthlyData || [])]?.sort((a, b) => {
      const aVal = a?.[sortField] ?? '';
      const bVal = b?.[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc' ? String(aVal)?.localeCompare(String(bVal)) : String(bVal)?.localeCompare(String(aVal));
    });
  }, [monthlyData, sortField, sortDir]);

  const totalPages = Math.ceil(sortedData?.length / PAGE_SIZE);
  const pageData = sortedData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const allColumns = [
    { key: 'period', label: t('creatorDetail.period') },
    { key: 'diamonds', label: 'Diamonds' },
    { key: 'live_days', label: 'Live Days' },
    { key: 'live_hours', label: 'Live Hours' },
    { key: 'tier_p', label: 'Tier' },
    { key: 'activeness_level', label: 'Activeness' },
    { key: 'total_bonus', label: 'Bonus Total' },
    { key: 'charge', label: 'Charge' },
    { key: 'cashback', label: 'Cashback' },
    { key: 'net_agency', label: 'Net Agence' },
  ];

  const columns = useMemo(() => {
    if (hideNetAgency) return allColumns.filter((c) => c.key !== 'net_agency');
    return allColumns;
  }, [hideNetAgency]);

  const renderCellValue = (row, key) => {
    switch (key) {
      case 'period':
        return (
          <span className="flex items-center gap-2 font-medium text-white/70">
            {row?.period || '—'}
            {row?.is_estimated && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)' }}>
                Estimé
              </span>
            )}
          </span>
        );
      case 'diamonds':
        return <span className="text-white font-semibold">💎 {formatDiamonds(row?.diamonds)}</span>;
      case 'live_days':
        return <span className="text-white/60">{row?.live_days ?? '—'}</span>;
      case 'live_hours':
        return <span className="text-white/60">{formatLiveHours(row?.live_hours)}</span>;
      case 'tier_p':
        return <TierBadge tier={row?.tier_p} />;
      case 'activeness_level':
        return <span className="text-white/60">{row?.activeness_level ?? '—'}</span>;
      case 'total_bonus':
        return <span className="text-white/70">{row?.total_bonus != null ? formatDollars(row.total_bonus) : '—'}</span>;
      case 'charge':
        return <span className="text-white/70">{row?.charge != null ? formatDollars(row.charge) : '—'}</span>;
      case 'cashback':
        return <span className="text-white/70">{row?.cashback != null ? formatDollars(row.cashback) : '—'}</span>;
      case 'net_agency':
        return <span className="text-white/70">{row?.net_agency != null ? formatDollars(row.net_agency) : '—'}</span>;
      default:
        return '—';
    }
  };

  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>
        {t('creatorDetail.monthlyHistory')}
      </h2>
      <div className="rounded-xl border p-4 mb-6" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <p className="text-xs uppercase tracking-wider mb-4" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>
          {t('creatorDetail.diamondsPerMonth')}
        </p>
        {loading ? (
          <div className="h-48 rounded animate-pulse" style={{ backgroundColor: '#1a1a1a' }} />
        ) : chartData?.length === 0 ? (
          <div className="h-48 flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.25)' }}>{t('common.noData')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
              <XAxis dataKey="period" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000)?.toFixed(0)}k` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="diamonds" radius={[3, 3, 0, 0]}>
                {chartData?.map((entry, i) => <Cell key={i} fill={TIER_COLORS?.[entry?.tier] || '#E5202A'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                {columns?.map((col) => (
                  <th
                    key={col?.key}
                    className="px-4 py-3 text-left font-semibold uppercase tracking-wider cursor-pointer hover:text-white select-none"
                    style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', backgroundColor: '#111111' }}
                    onClick={() => handleSort(col?.key)}
                  >
                    <span className="flex items-center gap-1">
                      {col?.label}
                      {sortField === col?.key && <span style={{ color: '#E5202A' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)]?.map((_, i) => (
                  <tr key={i}>
                    {columns?.map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: '60px' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pageData?.length === 0 ? (
                <tr>
                  <td colSpan={columns?.length} className="px-4 py-10 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                pageData?.map((row, i) => (
                  <tr key={i} className="border-t transition-colors" style={{ borderColor: '#1f1f1f', backgroundColor: row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.08)' : '#1a1a1a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent'; }}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        {renderCellValue(row, col.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Page {page} / {totalPages} — {sortedData?.length} entrées
            </span>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded text-xs border transition-colors disabled:opacity-30" style={{ borderColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' }}>←</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded text-xs border transition-colors disabled:opacity-30" style={{ borderColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' }}>→</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerMonthlyHistory;